import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import apiService from '../services/apiService';
import { RECON } from '../services/apiConstant';
import { VehicleNote } from '../types/vehicle';

interface CountResponse {
  count: number;
  success: boolean;
}

interface NotesResponse {
  notes: VehicleNote[];
  count: number;
  success: boolean;
}

interface AddNoteParams {
  vehicleInventoryId: string;
  note: string;
  userEmail: string;
}

interface AddNoteResponse {
  success: boolean;
  message?: string;
  note?: {
    note_id: string;
    [key: string]: unknown;
  };
}

interface UpdateNoteParams {
  noteId: string;
  note: string;
  vehicleInventoryId: string;
  userEmail: string;
}

interface UpdateNoteResponse {
  success: boolean;
  message?: string;
  note?: {
    note_id: string;
    [key: string]: unknown;
  };
}

interface DeleteNoteParams {
  noteId: string;
  vehicleInventoryId: string;
  userEmail: string;
}

interface DeleteNoteResponse {
  success: boolean;
  message?: string;
}

export const useReconRequestsCount = (
  vehicleInventoryId: string | undefined,
  options: Partial<UseQueryOptions<CountResponse>> = {}
) => {
  return useQuery<CountResponse>({
    queryKey: ['reconRequestsCount', vehicleInventoryId],
    queryFn: async () => {
      if (!vehicleInventoryId) {
        return { count: 0, success: true };
      }

      const response = await apiService.get<CountResponse>(`${RECON.reconRequests}?vehicle_inventory_id=${vehicleInventoryId}`);
      return {
        count: response.count || 0,
        success: response.success || false,
      };
    },
    enabled: !!vehicleInventoryId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    ...options,
  });
};

export const useVehicleNotesCount = (
  vehicleInventoryId: string | undefined,
  options: Partial<UseQueryOptions<CountResponse>> = {}
) => {
  return useQuery<CountResponse>({
    queryKey: ['vehicleNotesCount', vehicleInventoryId],
    queryFn: async () => {
      if (!vehicleInventoryId) {
        return { count: 0, success: true };
      }

      const response = await apiService.get<CountResponse>(`${RECON.vehicleNotes}?vehicle_inventory_id=${vehicleInventoryId}`);
      return {
        count: response.count || 0,
        success: response.success || false,
      };
    },
    enabled: !!vehicleInventoryId,
    staleTime: 1 * 60 * 1000,
    gcTime: 3 * 60 * 1000,
    retry: 1,
    ...options,
  });
};

export const useVehicleNotesWithCount = (
  vehicleInventoryId: string | undefined,
  options: Partial<UseQueryOptions<NotesResponse>> = {}
) => {
  return useQuery<NotesResponse>({
    queryKey: ['vehicleNotes', vehicleInventoryId],
    queryFn: async () => {
      if (!vehicleInventoryId) {
        return { notes: [], count: 0, success: true };
      }

      const response = await apiService.get<NotesResponse>(`${RECON.vehicleNotes}?vehicle_inventory_id=${vehicleInventoryId}`);
      return {
        notes: response.notes || [],
        count: response.count || 0,
        success: response.success || false,
      };
    },
    enabled: !!vehicleInventoryId,
    staleTime: 1 * 60 * 1000,
    gcTime: 3 * 60 * 1000,
    retry: 1,
    ...options,
  });
};

export const useAddVehicleNote = () => {
  const queryClient = useQueryClient();

  return useMutation<AddNoteResponse, Error, AddNoteParams>({
    mutationFn: async ({ vehicleInventoryId, note, userEmail }) => {
      const response = await apiService.post<AddNoteResponse>(RECON.vehicleNotes, {
        vehicle_inventory_id: vehicleInventoryId,
        note: note.trim(),
        user_email: userEmail,
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to add note');
      }

      return response;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['vehicleNotes', variables.vehicleInventoryId] });
      queryClient.invalidateQueries({ queryKey: ['vehicleNotesCount', variables.vehicleInventoryId] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-notes', variables.vehicleInventoryId] });

      // Invalidate vehicle history - use predicate to match all variations of entityTypes filter
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'vehicle-history' && key[1] === variables.vehicleInventoryId;
        }
      });
    },
  });
};

export const useUpdateVehicleNote = () => {
  const queryClient = useQueryClient();

  return useMutation<UpdateNoteResponse, Error, UpdateNoteParams>({
    mutationFn: async ({ noteId, note, userEmail }) => {
      const response = await apiService.put<UpdateNoteResponse>(RECON.updateVehicleNote(noteId), {
        note: note.trim(),
        changed_by: userEmail,
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update note');
      }

      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate vehicle notes queries
      queryClient.invalidateQueries({ queryKey: ['vehicleNotes', variables.vehicleInventoryId] });
      queryClient.invalidateQueries({ queryKey: ['vehicleNotesCount', variables.vehicleInventoryId] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-notes', variables.vehicleInventoryId] });

      // Invalidate entity attachments for this note
      queryClient.invalidateQueries({ queryKey: ['entity-attachments', 'note', variables.noteId] });

      // Invalidate vehicle history - use predicate to match all variations
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'vehicle-history' && key[1] === variables.vehicleInventoryId;
        }
      });
    },
  });
};

export const useDeleteVehicleNote = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteNoteResponse, Error, DeleteNoteParams>({
    mutationFn: async ({ noteId, userEmail }) => {
      const response = await apiService.delete<DeleteNoteResponse>(
        `${RECON.deleteVehicleNote(noteId)}?deleted_by=${encodeURIComponent(userEmail)}`
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete note');
      }

      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate vehicle notes queries
      queryClient.invalidateQueries({ queryKey: ['vehicleNotes', variables.vehicleInventoryId] });
      queryClient.invalidateQueries({ queryKey: ['vehicleNotesCount', variables.vehicleInventoryId] });
      queryClient.invalidateQueries({ queryKey: ['vehicle-notes', variables.vehicleInventoryId] });

      // Invalidate vehicle history - use predicate to match all variations
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'vehicle-history' && key[1] === variables.vehicleInventoryId;
        }
      });
    },
  });
};
